import type { Action, D20Face, GameEvent, GameState, Move, Player, Result, Ruleset, SaveFace } from './types';
import { initialBoard, material } from './board';
import { draw } from './rng';
import { applyMove, legalMoves as turnMoves } from './moves';

export const STANDARD_RULES: Ruleset = {
  faces: { '1': 8, '2': 6, '3': 2, skip: 2, reverse: 2 },
  saveSides: 6,
  saveFaces: 2,
  vetoes: 1,
  pawnRebuild: false,
  knightHeavy: false,
};

const FACE_ORDER: D20Face[] = ['1', '2', '3', 'skip', 'reverse'];
const otherPlayer = (p: Player): Player => (p === 'A' ? 'B' : 'A');

export function validateRules(r: Ruleset): void {
  const total = FACE_ORDER.reduce((n, f) => n + r.faces[f], 0);
  if (!FACE_ORDER.every((f) => Number.isInteger(r.faces[f]) && r.faces[f] >= 0)) throw new Error('Face counts must be whole numbers, 0 or more');
  if (total < 1) throw new Error('The die needs at least one face');
  if (r.faces['1'] + r.faces['2'] + r.faces['3'] < 1) throw new Error('The die needs at least one number face');
  if (!(Number.isInteger(r.saveSides) && r.saveSides >= 1)) throw new Error('Save die needs at least one side');
  if (!(Number.isInteger(r.saveFaces) && r.saveFaces >= 0 && r.saveFaces <= r.saveSides)) throw new Error('Save faces must be between 0 and the number of sides');
  if (!(Number.isInteger(r.vetoes) && r.vetoes >= 0)) throw new Error('Vetoes must be 0 or more');
}

export function newGame(seed: number, rules: Ruleset = STANDARD_RULES): GameState {
  validateRules(rules);
  return {
    rules,
    seed: seed >>> 0,
    rollIndex: 0,
    board: initialBoard(),
    control: { A: 'l', B: 'd' },
    turn: 'A',
    phase: 'roll',
    rolled: 0,
    movesLeft: 0,
    heavyUsed: false,
    movedKings: { l: false, d: false },
    hurt: null,
    vetoes: { A: rules.vetoes, B: rules.vetoes },
    winner: null,
  };
}

/** Moves the player to act may make now (empty outside the move phase). */
export function legalMoves(s: GameState): Move[] {
  if (s.phase !== 'move') return [];
  return turnMoves(s, s.control[s.turn], s.rules);
}

function passTurn(s: GameState): GameState {
  return {
    ...s,
    turn: otherPlayer(s.turn),
    phase: 'roll',
    rolled: 0,
    movesLeft: 0,
    heavyUsed: false,
    movedKings: { l: false, d: false },
    hurt: null,
  };
}

function swapControl(s: GameState): GameState {
  return { ...s, control: { A: s.control.B, B: s.control.A } };
}

function expectPhase(s: GameState, phase: GameState['phase']): void {
  if (s.phase !== phase) throw new Error(`Expected phase "${phase}" but the game is in "${s.phase}"`);
}

export function rollD20(s: GameState): Result {
  expectPhase(s, 'roll');
  const { faces } = s.rules;
  const total = FACE_ORDER.reduce((n, f) => n + faces[f], 0);
  let n = draw(s.seed, s.rollIndex, total);
  let face: D20Face = '1';
  for (const f of FACE_ORDER) {
    if (n < faces[f]) {
      face = f;
      break;
    }
    n -= faces[f];
  }
  const player = s.turn;
  const events: GameEvent[] = [{ type: 'rolled', player, face }];
  let state: GameState = { ...s, rollIndex: s.rollIndex + 1 };

  if (face === 'skip') {
    events.push({ type: 'skipped', player });
    return { state: passTurn(state), events };
  }
  if (face === 'reverse') {
    const a = material(state.board, state.control.A);
    const b = material(state.board, state.control.B);
    if (a === b) {
      events.push({ type: 'reverse-level', player }, { type: 'reversed' });
      return { state: passTurn(swapControl(state)), events };
    }
    const hurt: Player = a > b ? 'A' : 'B';
    events.push({ type: 'reverse-pending', player, hurt });
    return { state: { ...state, phase: 'save', hurt }, events };
  }
  const rolled = Number(face);
  state = { ...state, phase: 'move', rolled, movesLeft: rolled, heavyUsed: false, movedKings: { l: false, d: false } };
  if (legalMoves(state).length === 0) {
    events.push({ type: 'no-legal-moves', player });
    state = passTurn(state);
  }
  return { state, events };
}

function resolveReverse(s: GameState, cancelled: boolean, events: GameEvent[]): Result {
  events.push(cancelled ? { type: 'reverse-cancelled' } : { type: 'reversed' });
  return { state: passTurn(cancelled ? s : swapControl(s)), events };
}

export function rollSave(s: GameState): Result {
  expectPhase(s, 'save');
  const { saveSides, saveFaces } = s.rules;
  const face: SaveFace = draw(s.seed, s.rollIndex, saveSides) < saveFaces ? 'save' : 'blank';
  const events: GameEvent[] = [{ type: 'save-rolled', player: s.hurt!, face }];
  return resolveReverse({ ...s, rollIndex: s.rollIndex + 1 }, face === 'save', events);
}

/** The player a reverse would hurt spends a veto to cancel it for certain. */
export function useVeto(s: GameState): Result {
  expectPhase(s, 'save');
  const player = s.hurt!;
  if (s.vetoes[player] < 1) throw new Error('No veto left');
  const state = { ...s, vetoes: { ...s.vetoes, [player]: s.vetoes[player] - 1 } };
  return resolveReverse(state, true, [{ type: 'veto-used', player }]);
}

export function play(s: GameState, move: Move): Result {
  expectPhase(s, 'move');
  const legal = legalMoves(s).some((m) => m.kind === move.kind && m.to === move.to && (m.kind === 'add' || (move.kind === 'move' && m.from === move.from)));
  if (!legal) throw new Error('Illegal move');
  const player = s.turn;
  const n = applyMove(s, move, s.control[player], s.rules.knightHeavy);
  const events: GameEvent[] = [{ type: 'moved', player, move, captured: n.captured?.t ?? null }];
  let state: GameState = { ...s, board: n.board, movesLeft: n.movesLeft, heavyUsed: n.heavyUsed, movedKings: n.movedKings };
  if (n.captured?.t === 'K') {
    events.push({ type: 'game-over', winner: player });
    return { state: { ...state, phase: 'over', winner: player, movesLeft: 0 }, events };
  }
  if (state.movesLeft === 0) return { state: passTurn(state), events };
  if (legalMoves(state).length === 0) {
    events.push({ type: 'no-legal-moves', player });
    state = passTurn(state);
  }
  return { state, events };
}



export function applyAction(s: GameState, a: Action): Result {
  if (a.type === 'roll') return rollD20(s);
  if (a.type === 'save') return rollSave(s);
  if (a.type === 'veto') return useVeto(s);
  return play(s, a.move);
}
